import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions`);
      const data = await res.json();
      setSessions(data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const selectSession = async (sessionId) => {
    setSelectedSession(sessionId);
    setLoadingMessages(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`);
      const data = await res.json();
      setMessages(data);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="text-magenta hover:text-white transition-colors text-sm">&larr; Back to site</a>
          <span className="text-white/30">|</span>
          <h1 className="text-lg font-semibold">Chat Logs</h1>
        </div>
        <button
          onClick={fetchSessions}
          data-testid="refresh-sessions-btn"
          className="text-sm text-gray-400 hover:text-magenta transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="flex h-[calc(100vh-57px)]">
        {/* Sessions sidebar */}
        <div className="w-80 border-r border-white/10 overflow-y-auto flex-shrink-0" data-testid="sessions-list">
          {loadingSessions ? (
            <div className="p-6 text-center text-gray-500 text-sm">Loading conversations...</div>
          ) : sessions.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No conversations yet</div>
          ) : (
            sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => selectSession(s.session_id)}
                data-testid={`session-${s.session_id}`}
                className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  selectedSession === s.session_id ? 'bg-magenta/10 border-l-2 border-l-magenta' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">{formatTime(s.created_at)}</span>
                  <span className="text-xs text-magenta/70">{s.message_count || 0} msgs</span>
                </div>
                <p className="text-sm text-gray-300 truncate">{s.preview || 'No messages'}</p>
              </button>
            ))
          )}
        </div>

        {/* Message view */}
        <div className="flex-1 overflow-y-auto" data-testid="messages-view">
          {!selectedSession ? (
            <div className="h-full flex items-center justify-center text-gray-600 text-sm">
              Select a conversation to view
            </div>
          ) : loadingMessages ? (
            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
              Loading messages...
            </div>
          ) : (
            <div className="max-w-3xl mx-auto py-6 px-6 space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${msg.role === 'user' ? '' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${msg.role === 'user' ? 'text-magenta' : 'text-gray-500'}`}>
                        {msg.role === 'user' ? 'Visitor' : "Ryan's Assistant"}
                      </span>
                      <span className="text-xs text-gray-600">{formatTime(msg.timestamp)}</span>
                    </div>
                    <div
                      className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-magenta/20 text-white rounded-br-md'
                          : 'bg-white/5 text-gray-300 rounded-bl-md'
                      }`}
                      dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
